import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePreAuthFeatureFlags } from '../../hooks/usePreAuthFeatureFlags';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import FileUpload from '../../components/common/FileUpload';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import EmployerHoldingPage from './EmployerHoldingPage';
import { 
  Shield, Building, User, Briefcase, Check, ChevronRight, ChevronLeft, 
  Globe, Phone, MapPin, Users, Search, FileText, Loader2
} from 'lucide-react';

const INDUSTRIES = [
  "Technology", "Finance", "Healthcare", "Education", "Retail",
  "Manufacturing", "Consulting", "Marketing", "Legal", "Real Estate",
  "Hospitality", "Transportation", "Energy", "Media", "Non-profit"
];

const COMPANY_SIZES = [
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-1000", label: "201-1000 employees" },
  { value: "1000+", label: "1000+ employees" }
];

const DEPARTMENTS = [
  "HR", "Talent Acquisition", "Operations", "Founder/CEO", "Office Management", "Other"
];

const HIRES_PER_YEAR = [
  { value: "1-5", label: "1-5 people" },
  { value: "6-20", label: "6-20 people" },
  { value: "21-50", label: "21-50 people" },
  { value: "51-200", label: "51-200 people" },
  { value: "200+", label: "200+ people" }
];

const HIRING_CHALLENGES = [
  { value: "finding_candidates", label: "Finding qualified candidates" },
  { value: "screening_time", label: "Screening takes too long" },
  { value: "unreliable_references", label: "References are unreliable" },
  { value: "high_turnover", label: "High turnover after hiring" },
  { value: "recruiting_cost", label: "Cost of recruiting firms" },
  { value: "other", label: "Other" }
];

const HOW_HEARD = [
  "LinkedIn", "AppSumo", "Google Search", "Word of mouth", "Other"
];

const EmployerOnboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  
  // A5: Feature flag gating state
  const [employerPlatformLive, setEmployerPlatformLive] = useState(null);
  const [gatingChecked, setGatingChecked] = useState(false);
  
  // FCRA Acknowledgment (Step 4)
  const [fcraAccepted, setFcraAccepted] = useState(false);
  
  // Step 1: Company Details
  const [companyName, setCompanyName] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [headquarters, setHeadquarters] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  
  // Step 2: Account Owner Details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [otherDepartment, setOtherDepartment] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Step 3: Hiring Context
  const [hiresPerYear, setHiresPerYear] = useState('');
  const [frequentRoles, setFrequentRoles] = useState('');
  const [hiringChallenges, setHiringChallenges] = useState([]);
  const [howHeard, setHowHeard] = useState('');

  // A5: Use shared pre-auth feature flags hook for employer platform gating
  const { isEmployerPlatformLive, loading: flagsLoading } = usePreAuthFeatureFlags();
  
  // Set gating state based on hook results
  useEffect(() => {
    if (!flagsLoading) {
      setEmployerPlatformLive(isEmployerPlatformLive());
      setGatingChecked(true);
    }
  }, [flagsLoading, isEmployerPlatformLive]);

  useEffect(() => {
    // Only check onboarding after gating is resolved
    if (gatingChecked && employerPlatformLive !== false) {
      checkOnboardingStatus();
    }
  }, [gatingChecked, employerPlatformLive]);

  const checkOnboardingStatus = async () => {
    try {
      const res = await employerAPI.getProfile();
      if (res.data.onboarding_complete) {
        // User has already completed onboarding, redirect to dashboard
        navigate('/employer/dashboard', { replace: true });
        return;
      }
      // Pre-fill any existing data
      if (res.data.company_name) setCompanyName(res.data.company_name);
      if (res.data.logo_url) setCompanyLogo(res.data.logo_url);
      if (res.data.industry) setIndustry(res.data.industry);
      if (res.data.company_size || res.data.size) setCompanySize(res.data.company_size || res.data.size);
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    } finally {
      setCheckingOnboarding(false);
    }
  };

  const totalSteps = 4;
  const canSkip = currentStep > 1 && currentStep < 4;

  const handleNext = () => {
    if (currentStep === 1) {
      if (!companyName.trim()) {
        toast.error('Company name is required');
        return;
      }
      if (!industry) {
        toast.error('Industry is required');
        return;
      }
      if (!companySize) {
        toast.error('Company size is required');
        return;
      }
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    if (canSkip) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await employerAPI.updateProfile({
        company_name: companyName,
        logo_url: companyLogo || null,
        industry,
        company_size: companySize,
        headquarters: headquarters || null,
        website: website || null,
        description: description || null,
        // Account owner details
        owner_first_name: firstName || null,
        owner_last_name: lastName || null,
        owner_job_title: jobTitle || null,
        owner_department: department === 'Other' ? otherDepartment : department || null,
        owner_work_email: workEmail || null,
        owner_phone: phoneNumber || null,
        // Hiring context
        hires_per_year: hiresPerYear || null,
        frequent_roles: frequentRoles || null,
        hiring_challenges: hiringChallenges.length > 0 ? hiringChallenges : null,
        how_heard: howHeard || null,
        // FCRA Compliance
        fcra_accepted: true,
        fcra_accepted_at: new Date().toISOString(),
        // Mark complete
        onboarding_complete: true
      });
      
      toast.success('Account setup complete!');
      navigate('/employer/dashboard');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const toggleChallenge = (value) => {
    setHiringChallenges(prev => 
      prev.includes(value) 
        ? prev.filter(c => c !== value)
        : [...prev, value]
    );
  };

  // A5: Show holding page if employer platform is not live (for non-superadmins)
  if (gatingChecked && employerPlatformLive === false && !user?.is_superadmin) {
    return <EmployerHoldingPage />;
  }

  // Show loading while checking feature flags or onboarding
  if (!gatingChecked || checkingOnboarding) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B5AFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF] px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="h-8 w-8 text-[#1B5AFF]" />
            <span className="text-xl font-bold text-[#0A0F1E]">VETT</span>
          </div>
          <h1 className="text-2xl font-bold text-[#0A0F1E]">Set up your account</h1>
          <p className="text-[#5B6472] mt-2">Let's get you started in just a few steps</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#5B6472] text-sm">Step {currentStep} of {totalSteps}</span>
            {canSkip && (
              <button 
                onClick={handleSkip}
                className="text-[#5B6472] text-sm hover:text-[#0A0F1E] transition-colors"
              >
                Skip this step
              </button>
            )}
          </div>
          <div className="h-2 bg-[#FFFFFF] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#1B5AFF] transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Step 1: Company Details */}
        {currentStep === 1 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in" data-testid="onboarding-step-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#1B5AFF]/20 flex items-center justify-center">
                <Building className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Company Details</h2>
                <p className="text-[#5B6472] text-sm">Tell us about your company</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-[#0A0F1E]">Company Name *</Label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your company name"
                  className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  data-testid="company-name-input"
                />
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Company Logo (Optional)</Label>
                <FileUpload
                  value={companyLogo}
                  onChange={setCompanyLogo}
                  placeholder="Upload company logo"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Industry *</Label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    {INDUSTRIES.map(ind => (
                      <SelectItem key={ind} value={ind} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                        {ind}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Company Size *</Label>
                <Select value={companySize} onValueChange={setCompanySize}>
                  <SelectTrigger className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    {COMPANY_SIZES.map(size => (
                      <SelectItem key={size.value} value={size.value} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                        {size.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Headquarters (City, Country)</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5B6472]" />
                  <Input
                    value={headquarters}
                    onChange={(e) => setHeadquarters(e.target.value)}
                    placeholder="San Francisco, USA"
                    className="mt-1 pl-10 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Company Website (Optional)</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5B6472]" />
                  <Input
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://yourcompany.com"
                    className="mt-1 pl-10 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Company Description (Optional)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 300))}
                  placeholder="Brief description of your company..."
                  className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[80px]"
                  maxLength={300}
                />
                <div className="text-right text-[#5B6472] text-xs mt-1">{description.length}/300</div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 2: Account Owner Details */}
        {currentStep === 2 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in" data-testid="onboarding-step-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#1B5AFF]/20 flex items-center justify-center">
                <User className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Account Owner Details</h2>
                <p className="text-[#5B6472] text-sm">Tell us about yourself</p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#0A0F1E]">First Name</Label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  />
                </div>
                <div>
                  <Label className="text-[#0A0F1E]">Last Name</Label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Smith"
                    className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Job Title</Label>
                <Input
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="HR Manager"
                  className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                />
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Department</Label>
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTrigger className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    {DEPARTMENTS.map(dept => (
                      <SelectItem key={dept} value={dept} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {department === 'Other' && (
                  <Input
                    value={otherDepartment}
                    onChange={(e) => setOtherDepartment(e.target.value)}
                    placeholder="Specify department"
                    className="mt-2 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  />
                )}
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Direct Work Email</Label>
                <Input
                  type="email"
                  value={workEmail}
                  onChange={(e) => setWorkEmail(e.target.value)}
                  placeholder="john@company.com"
                  className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                />
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Phone Number (Optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5B6472]" />
                  <Input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="mt-1 pl-10 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Hiring Context */}
        {currentStep === 3 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in" data-testid="onboarding-step-3">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#1B5AFF]/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#1B5AFF]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Hiring Context</h2>
                <p className="text-[#5B6472] text-sm">Help us understand your hiring needs</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-[#0A0F1E]">How many people do you typically hire per year?</Label>
                <Select value={hiresPerYear} onValueChange={setHiresPerYear}>
                  <SelectTrigger className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    {HIRES_PER_YEAR.map(option => (
                      <SelectItem key={option.value} value={option.value} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">Which roles do you hire most frequently?</Label>
                <Textarea
                  value={frequentRoles}
                  onChange={(e) => setFrequentRoles(e.target.value.slice(0, 200))}
                  placeholder="e.g., Software Engineers, Sales Representatives, Customer Support..."
                  className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[80px]"
                  maxLength={200}
                />
                <div className="text-right text-[#5B6472] text-xs mt-1">{frequentRoles.length}/200</div>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">What is your biggest hiring challenge? (Select all that apply)</Label>
                <div className="mt-2 space-y-2">
                  {HIRING_CHALLENGES.map(challenge => (
                    <button
                      key={challenge.value}
                      type="button"
                      onClick={() => toggleChallenge(challenge.value)}
                      className={`w-full p-3 rounded-xl border text-left transition-all ${
                        hiringChallenges.includes(challenge.value)
                          ? 'border-[#1B5AFF] bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]'
                          : 'border-[#E8EDF5] bg-[#F5F8FF]/30 text-[#5B6472] hover:border-[#1B5AFF]/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{challenge.label}</span>
                        {hiringChallenges.includes(challenge.value) && (
                          <Check className="w-4 h-4 text-[#1B5AFF]" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[#0A0F1E]">How did you hear about VETT?</Label>
                <Select value={howHeard} onValueChange={setHowHeard}>
                  <SelectTrigger className="mt-1 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                    {HOW_HEARD.map(option => (
                      <SelectItem key={option} value={option} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        )}

        {/* Step 4: Confirmation */}
        {currentStep === 4 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 animate-fade-in text-center" data-testid="onboarding-step-4">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-[#0A0F1E] mb-2">Your VETT account is ready.</h2>
            <p className="text-[#5B6472] mb-8">Start finding verified talent today.</p>

            {/* Summary */}
            <div className="bg-[#F5F8FF]/50 rounded-xl p-4 mb-6 text-left">
              <h3 className="text-sm font-medium text-[#5B6472] mb-3">Company Profile Summary</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Building className="w-4 h-4 text-[#1B5AFF]" />
                  <span className="text-[#0A0F1E]">{companyName}</span>
                </div>
                {industry && (
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-4 h-4 text-[#5B6472]" />
                    <span className="text-[#5B6472]">{industry}</span>
                  </div>
                )}
                {companySize && (
                  <div className="flex items-center gap-3">
                    <Users className="w-4 h-4 text-[#5B6472]" />
                    <span className="text-[#5B6472]">{COMPANY_SIZES.find(s => s.value === companySize)?.label}</span>
                  </div>
                )}
                {headquarters && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-[#5B6472]" />
                    <span className="text-[#5B6472]">{headquarters}</span>
                  </div>
                )}
              </div>
            </div>

            {/* FCRA Permissible Purpose Acknowledgment */}
            <div className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5 mb-6 text-left">
              <p className="text-[#1B5AFF] text-xs font-semibold uppercase tracking-widest mb-3">
                Required: US Legal Compliance
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fcraAccepted}
                  onChange={(e) => setFcraAccepted(e.target.checked)}
                  className="mt-1 flex-shrink-0 accent-[#1B5AFF]"
                  data-testid="fcra-acknowledgment-checkbox"
                />
                <span className="text-[#0A0F1E] text-sm leading-relaxed">
                  I acknowledge that VETT records constitute consumer reports under the Fair Credit Reporting Act (FCRA).
                  I will only use VETT to evaluate candidates for positions I am actively and legitimately hiring for
                  (permissible purpose). When I decline to hire a candidate based on information obtained through VETT,
                  I will provide that candidate with an adverse action notice including a copy of their VETT record and
                  a summary of their FCRA rights before taking final adverse action. I understand that misuse of consumer
                  reports is a violation of federal law.
                </span>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => { handleComplete().then(() => navigate('/employer/search')); }}
                disabled={loading || !fcraAccepted}
                className="flex-1 bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl py-6 btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="search-talent-btn"
              >
                <Search className="w-5 h-5 mr-2" />
                Search Talent
              </Button>
              <Button
                onClick={() => { handleComplete().then(() => navigate('/employer/records/new')); }}
                disabled={loading || !fcraAccepted}
                variant="outline"
                className="flex-1 border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-record-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Submit Your First Record
              </Button>
            </div>
          </Card>
        )}

        {/* Navigation Buttons */}
        {currentStep < 4 && (
          <div className="flex justify-between mt-6">
            <Button
              onClick={handleBack}
              disabled={currentStep === 1}
              variant="outline"
              className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              data-testid="next-step-btn"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployerOnboarding;
